import { calloutPdfBlock } from './document-editor/calloutPdf';
import { sanitizeRichTextHtml } from '../utils/sanitizeHtml';
import { parseEmbedSrc, videoEmbedWatchUrl } from '../utils/videoEmbeds';
import { resolveExportBodyHtml } from '../utils/resolveDocMediaForExport';

/**
 * Document → PDF, as a real file.
 *
 * WHY NOT THE PRINT DIALOG. "Export → PDF" sat next to "Markdown" and "HTML",
 * which both open a save dialog and write a file — and then opened a PRINT
 * dialog instead, making the user find "Save as PDF" in an OS menu. Print is
 * its own menu entry now; Export means a file on disk.
 *
 * WHY NOT html2canvas/jsPDF. That route RASTERISES: the text stops being text,
 * so it cannot be selected, searched or copied, and it blurs on zoom. For a
 * document export that is a worse artifact than the print dialog already
 * produced.
 *
 * WHY THIS WORKS AT ALL. Arbitrary HTML → PDF needs a layout engine. These
 * documents are not arbitrary: they come from the Lexical editor, whose output
 * is a bounded set of block elements (headings, paragraphs, lists, quotes,
 * code, rules, images, links). Walking THAT into pdfmake's document model is a
 * mapping, not a layout engine — and the result is a genuine text PDF.
 *
 * The HTML is sanitized on the way in — the same gate the editor and the print
 * overlay use. A document can carry content pasted from anywhere.
 */

const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

/**
 * The font table out of whatever shape `vfs_fonts` exports.
 *
 * pdfmake has moved this THREE times: `pdfMake.vfs` (0.1), a `default` export
 * (0.2 under bundler interop), and in 0.3 the module IS the table, keyed by
 * font filename. Guessing a path silently yields `undefined`, and the failure
 * surfaces far away as "File 'Roboto-Medium.ttf' not found in virtual file
 * system" — which is exactly how this broke first time.
 *
 * So identify it by SHAPE instead: the table is the object that actually has
 * a Roboto entry. That survives the next move too.
 */
function resolveVfs(mod) {
  const looksLikeVfs = (o) =>
    o && typeof o === 'object' && Object.keys(o).some((k) => /^Roboto-.*\.ttf$/i.test(k));
  for (const candidate of [mod, mod?.default, mod?.pdfMake?.vfs, mod?.default?.pdfMake?.vfs, mod?.vfs]) {
    if (looksLikeVfs(candidate)) return candidate;
  }
  return null;
}

/** pdfmake, its fonts, and the fonts' vfs — all lazy, none of it on the main bundle. Exported for testing. */
export async function loadPdfMake() {
  const [pdfMakeMod, fontsMod] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);
  const pdfMake = pdfMakeMod?.default || pdfMakeMod;
  const vfs = resolveVfs(fontsMod);
  if (!vfs) {
    // Fail with the CAUSE rather than letting pdfmake report a missing font
    // file several layers down, which reads as a corrupt install.
    throw new Error('PDF fonts failed to load (pdfmake vfs shape not recognised).');
  }
  // `pdfMake.vfs = …` DOES NOTHING in 0.3. The property is `virtualfs`, and it
  // is a VirtualFileSystem INSTANCE (js/virtual-fs.js) holding a private
  // `storage` map — assigning a plain object to `.vfs` writes a field nothing
  // reads, and the failure surfaces as pdfmake's own "File 'Roboto-Medium.ttf'
  // not found in virtual file system" from `readFileSync`. Register each font
  // through the instance's public API instead.
  //
  // Base64 is the encoding vfs_fonts ships, and `readFileSync` decodes from the
  // stored Buffer, so it must go in as base64 rather than as a raw string.
  if (typeof pdfMake.virtualfs?.writeFileSync === 'function') {
    for (const [name, contents] of Object.entries(vfs)) {
      if (typeof contents === 'string') pdfMake.virtualfs.writeFileSync(name, contents, 'base64');
    }
  } else {
    // 0.1/0.2 shape, kept so a downgrade does not silently produce no fonts.
    pdfMake.vfs = vfs;
  }
  // 0.3 no longer assumes Roboto — name the family the styles actually use.
  pdfMake.fonts = {
    Roboto: {
      normal: 'Roboto-Regular.ttf',
      bold: 'Roboto-Medium.ttf',
      italics: 'Roboto-Italic.ttf',
      bolditalics: 'Roboto-MediumItalic.ttf',
    },
  };
  return pdfMake;
}

const textOf = (node) => (node.textContent || '').replace(/\s+/g, ' ');

/**
 * Inline runs for one block, carrying bold/italic/link/code.
 *
 * pdfmake takes an array of `{ text, ...style }`, so the walk is depth-first
 * accumulating marks. Anything unrecognised contributes its text unstyled —
 * losing a mark is acceptable, losing the words is not.
 */
function inlineRuns(node, inherited = {}) {
  const runs = [];
  for (const child of node.childNodes) {
    if (child.nodeType === 3) {
      const text = child.textContent;
      if (text) runs.push({ text, ...inherited });
      continue;
    }
    if (child.nodeType !== 1) continue;
    const tag = child.tagName.toLowerCase();
    const marks = { ...inherited };
    if (tag === 'b' || tag === 'strong') marks.bold = true;
    if (tag === 'i' || tag === 'em') marks.italics = true;
    if (tag === 'u') marks.decoration = 'underline';
    if (tag === 's' || tag === 'strike' || tag === 'del') marks.decoration = 'lineThrough';
    if (tag === 'code') {
      marks.background = '#f4f4f5';
    }
    if (tag === 'a') {
      const href = child.getAttribute('href') || '';
      // Only http(s) becomes a live link. A javascript:/data: href in a PDF
      // annotation is the same class of problem it is in a page, and the
      // sanitizer upstream is not a licence to stop checking here.
      if (/^https?:\/\//i.test(href)) {
        marks.link = href;
        marks.color = '#4f46e5';
        marks.decoration = 'underline';
      }
    }
    if (tag === 'br') {
      runs.push({ text: '\n', ...marks });
      continue;
    }
    runs.push(...inlineRuns(child, marks));
  }
  return runs;
}

function listItems(node, ordered, depth = 0) {
  // To-do lists: saved HTML carries `__lexicallisttype="check"` on the <ul>
  // and `aria-checked` on each task <li> (the sanitizer preserves both).
  const isChecklist =
    !ordered &&
    (node.getAttribute('__lexicallisttype') === 'check' ||
      Array.from(node.children).some(
        (li) => li.tagName?.toLowerCase() === 'li' && li.hasAttribute('aria-checked')
      ));
  const items = [];
  const trailing = [];
  for (const li of node.children) {
    if (li.tagName?.toLowerCase() !== 'li') continue;
    // A nested list becomes its own INDENTED block after this one, rather than
    // recursing into pdfmake's nested-list shape (whose behaviour has moved
    // between versions). It used to be `remove()`d and never re-added, so every
    // sub-item was silently deleted from the PDF — the worst failure an export
    // can have, and invisible because the flat-list test never nested anything.
    const nested = li.querySelector('ol, ul');
    if (nested) {
      nested.remove();
      const sub = listItems(nested, nested.tagName.toLowerCase() === 'ol', depth + 1);
      for (const block of sub) {
        trailing.push({ ...block, margin: [20 * (depth + 1), 0, 0, 4] });
      }
    }
    if (isChecklist) {
      const runs = inlineRuns(li);
      // An ORPHANED WRAPPER <li> — no aria-checked, no text of its own, it
      // existed only to hold the nested list pulled out above (a sub-list
      // whose parent item was deleted). Real task items ALWAYS carry
      // aria-checked (@lexical/list stamps it on every leaf), so this shape
      // must not become a phantom empty '[ ] ' line. The editor CSS suppresses
      // the same wrapper's checkbox; the exporters forgot the twin guard.
      if (!li.hasAttribute('aria-checked') && runs.length === 0) continue;
      // ASCII '[x] '/'[ ] ', not ☑/☐: coverage of the ballot-box code points
      // in pdfmake's bundled Roboto faces is unverified, and a missing glyph
      // renders as NOTHING — the task state would silently vanish from the
      // export. Inline formatting still flows through inlineRuns.
      const box = li.getAttribute('aria-checked') === 'true' ? '[x] ' : '[ ] ';
      items.push({ text: [{ text: box }, ...runs], margin: [0, 0, 0, 2] });
    } else {
      items.push({ text: inlineRuns(li) });
    }
  }
  if (!items.length) return trailing;
  if (isChecklist) {
    // A `stack` of text lines rather than a pdfmake ul: the box prefix IS the
    // marker, and a ul would draw a bullet in front of every one. Same outer
    // margin as a list block, so nested-list indentation (the `trailing`
    // margin override above) treats both shapes identically.
    return [{ stack: items, margin: [0, 4, 0, 8] }, ...trailing];
  }
  return [{ [ordered ? 'ol' : 'ul']: items, margin: [0, 4, 0, 8] }, ...trailing];
}

function imageBlock(node) {
  const src = node.getAttribute('src') || '';
  // Only already-embedded images. A remote URL would need a fetch, and a
  // document export that silently makes network requests to whatever a pasted
  // <img> points at is a tracking pixel with extra steps. Encrypted document
  // images are pre-resolved into data: URIs by renderDocumentPdf (via
  // utils/resolveDocMediaForExport.js, which fetches OUR hosts only — same
  // stance), so by the time this mapper runs, an embeddable image is data: and
  // everything else is still refused here.
  if (!/^data:image\//i.test(src)) return [];
  if (src.length > MAX_IMAGE_BYTES) return [];
  return [{ image: src, width: 460, margin: [0, 6, 0, 10] }];
}

/**
 * A video embed becomes a LINK LINE — a PDF cannot host a player, and a dead
 * grey rectangle where the video was is the print-view failure this feature
 * exists to fix. The iframe src is parsed (never echoed) and rebuilt as the
 * canonical watch url; an iframe that does not parse maps to nothing (the
 * sanitizer upstream already kills those — this guard holds independently,
 * same belt-and-braces as imageBlock's data:-only check).
 */
function videoLinkBlock(node) {
  const parsed = parseEmbedSrc(node.getAttribute('src') || '');
  const watchUrl = parsed ? videoEmbedWatchUrl(parsed) : null;
  if (!watchUrl) return [];
  return [{
    text: [
      { text: '▶ Watch video: ', color: '#52525b' },
      // Same live-link styling inlineRuns gives an https <a>.
      { text: watchUrl, link: watchUrl, color: '#4f46e5', decoration: 'underline' },
    ],
    margin: [0, 6, 0, 10],
  }];
}

/**
 * A code block's box: a filled rectangle with a thin border, no inner grid.
 *
 * pdfmake layouts are callbacks, not a style object — `hLineWidth`/`vLineWidth`
 * return the border width for each line INDEX, so returning 0 only for the
 * interior is how you get an outline without a grid. With one cell there is no
 * interior, but the functions must still handle every index or pdfmake throws.
 */
const CODE_BLOCK_LAYOUT = {
  hLineWidth: () => 0.7,
  vLineWidth: () => 0.7,
  hLineColor: () => '#e4e4e7',
  vLineColor: () => '#e4e4e7',
  fillColor: () => '#f4f4f5',
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
};

/**
 * A table's grid: hairlines all round, header row tinted.
 *
 * `fillColor` receives the OUTER node — the one carrying `.table` — not the
 * inner table object (pdfmake `js/tableProcessor.js`, which calls
 * `layout.fillColor(rowIndex, this.tableNode, colIndex)`). An earlier version
 * read a `__headerRow` marker written INSIDE `.table`, so it was `undefined` on
 * every call and the tint never rendered at all. Reading `headerRows`, which
 * pdfmake already carries, means there is no private marker to get this wrong
 * about a second time.
 */
const TABLE_LAYOUT = {
  hLineWidth: () => 0.7,
  vLineWidth: () => 0.7,
  hLineColor: () => '#d4d4d8',
  vLineColor: () => '#d4d4d8',
  fillColor: (rowIndex, node) =>
    rowIndex < (node?.table?.headerRows || 0) ? '#f4f4f5' : null,
};

/**
 * Widest table this mapping will build.
 *
 * Enforced on the ROW TOTAL, not per cell. It was per cell, and the comment
 * claimed it bounded memory — it did not: 40 cells each clamped to colspan 64
 * still built 2 560 columns, and a 130 KB paste produced 230 400 columns and
 * 13.8 million cell objects in ~3s before pdfmake was even called. Clamping the
 * part while the whole is unbounded is not a bound.
 *
 * Cells past the cap are DROPPED rather than squeezed: a table this wide is
 * already unreadable, and the alternative is columns too narrow to render.
 */
const MAX_TABLE_COLUMNS = 64;

const clamp = (n, max) => (Number.isFinite(n) ? Math.min(Math.max(1, n), Math.max(1, max)) : 1);

/**
 * One cell's pdfmake content.
 *
 * A cell containing a nested table becomes a STACK of its own text followed by
 * the nested table, rather than having the inner text flattened in beside it.
 */
function cellContent(cell) {
  // NESTED AT ANY DEPTH, not just as a direct child. `cell.children` missed a
  // `<div>`-wrapped table — which is how most pasted web tables arrive, and the
  // sanitizer allows `div` — so the inner grid was lost and its text merged
  // into the outer cell: exactly the run-on merge this mapping exists to avoid.
  // Scoped by `closest`, so a table nested two levels down belongs to the cell
  // that directly contains it and is not claimed twice.
  const nested = Array.from(cell.querySelectorAll('table')).filter(
    (t) => t.parentElement?.closest('td, th') === cell
  );
  // Carried through to the PDF. The palette in the editor is justified by these
  // colours being printable, so dropping them here made that claim false.
  const fill = cellFillColor(cell);
  if (!nested.length) {
    return compactCell({ text: inlineRuns(cell), margin: [4, 3, 4, 3] }, fill);
  }

  const clone = cell.cloneNode(true);
  for (const t of Array.from(clone.querySelectorAll('table'))) t.remove();
  const own = inlineRuns(clone);
  const stack = [];
  if (own.length) stack.push({ text: own });
  for (const t of nested) stack.push(...tableBlock(t));
  return compactCell({ stack, margin: [4, 3, 4, 3] }, fill);
}

/** pdfmake takes `fillColor` on the CELL, which is simpler than a layout callback. */
function compactCell(node, fill) {
  return fill ? { ...node, fillColor: fill } : node;
}

/**
 * A cell's background, if it has one this export can trust.
 *
 * Only the literal forms the sanitizer emits (`rgb()`/`rgba()`/hex). Anything
 * else is ignored rather than passed to pdfmake, which does not validate colours
 * and throws deep inside layout on a value it cannot parse — turning one odd
 * cell into a failed document export.
 */
function cellFillColor(cell) {
  const value = (cell.style?.backgroundColor || '').trim();
  if (!value) return null;
  if (/^#[0-9a-f]{6}$/i.test(value)) return value;
  if (/^#[0-9a-f]{3}$/i.test(value)) {
    return `#${value.slice(1).split('').map((ch) => ch + ch).join('')}`;
  }
  const m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+%?))?/i.exec(value);
  if (!m) return null;

  // ALPHA IS COMPOSITED OVER WHITE, not discarded. Reading three components and
  // ignoring the fourth turned `rgba(0,0,0,0.05)` — a 5% zebra stripe, one of
  // the commonest cell backgrounds on the web and something that arrives by
  // paste — into SOLID BLACK behind black text. pdfmake accepts the value, so
  // nothing throws; the page is just unreadable.
  //
  // White because that is the page. A PDF has no transparency model here to
  // blend against anything else.
  const alphaRaw = m[4];
  const alpha =
    alphaRaw == null
      ? 1
      : Math.max(0, Math.min(1, alphaRaw.endsWith('%') ? parseFloat(alphaRaw) / 100 : parseFloat(alphaRaw)));
  if (!Number.isFinite(alpha)) return null;

  const over = (n) => {
    const c = Math.max(0, Math.min(255, Number(n)));
    return Math.round(c * alpha + 255 * (1 - alpha));
  };
  const hex = (n) => n.toString(16).padStart(2, '0');
  return `#${hex(over(m[1]))}${hex(over(m[2]))}${hex(over(m[3]))}`;
}


/**
 * A real pdfmake table.
 *
 * REPLACES A FLATTENING. This used to return `{ text: textOf(node) }` — every
 * table in a document became one run-on paragraph. The justification at the
 * time was that column widths need measurement and a mangled table is worse
 * than a readable paragraph. That trade was wrong once tables became a feature
 * people use: `widths: '*'` divides the page evenly and needs no measurement at
 * all, and a paragraph of merged cells is not "readable", it is unrecoverable.
 *
 * COLSPAN/ROWSPAN ARE HONOURED, because Lexical writes them and dropping them
 * shifts every following cell one column left — a silently wrong table, which
 * is the failure this mapping is supposed to avoid. pdfmake wants the spanned
 * positions present as `{}` placeholders, so they are inserted explicitly.
 *
 * A table pdfmake would reject (no rows, or ragged rows it cannot lay out)
 * falls back to the old flattening rather than throwing — losing the grid beats
 * losing the export.
 */
function tableBlock(node) {
  // SCOPED to this table. `querySelectorAll('tr')` is unscoped, so a nested
  // table's rows became rows of the OUTER table while its text was also pulled
  // into the outer cell by `inlineRuns` — the same content twice, and the outer
  // cell reduced to the run-on merge this mapping exists to avoid. Nested
  // tables reach here by paste: the sanitizer allows `table` with no nesting
  // limit.
  const rows = Array.from(node.querySelectorAll('tr')).filter(
    (tr) => tr.closest('table') === node
  );
  if (!rows.length) return [];

  const grid = [];
  let headerRow = false;

  rows.forEach((tr, r) => {
    const cells = Array.from(tr.children).filter((c) => /^(td|th)$/i.test(c.tagName));
    if (!cells.length) return;
    if (r === 0 && cells.every((c) => c.tagName.toLowerCase() === 'th')) headerRow = true;

    grid[r] = grid[r] || [];
    let c = 0;
    for (const cell of cells) {
      // Skip past positions already claimed by a rowspan from above.
      while (grid[r][c] !== undefined) c += 1;

      // CLAMPED to what the table can actually hold. HTML permits
      // `rowspan="65534"`, browsers keep it, and Lexical round-trips it — so a
      // pasted cell could materialise 65 534 placeholder rows, which measured
      // ~10s and a 3.9 MB PDF for one 50-byte cell. A span past the last row is
      // meaningless anyway; the HTML spec already treats it as reaching the end.
      // Room LEFT in the row, so the total cannot exceed the cap however many
      // cells conspire.
      if (c >= MAX_TABLE_COLUMNS) break;
      const colSpan = clamp(parseInt(cell.getAttribute('colspan'), 10), MAX_TABLE_COLUMNS - c);
      const rowSpan = clamp(parseInt(cell.getAttribute('rowspan'), 10), rows.length - r);

      const content = cellContent(cell);
      if (cell.tagName.toLowerCase() === 'th') content.bold = true;
      if (colSpan > 1) content.colSpan = colSpan;
      if (rowSpan > 1) content.rowSpan = rowSpan;

      grid[r][c] = content;
      // pdfmake needs the spanned positions to EXIST as empty objects; without
      // them every later cell in the row shifts left.
      for (let i = 1; i < colSpan; i += 1) grid[r][c + i] = {};
      for (let j = 1; j < rowSpan; j += 1) {
        grid[r + j] = grid[r + j] || [];
        for (let i = 0; i < colSpan; i += 1) grid[r + j][c + i] = {};
      }
      c += colSpan;
    }
  });

  const body = grid.filter(Boolean);
  if (!body.length) return [];

  // Ragged rows crash pdfmake's layout. Pad to the widest so a hand-edited or
  // pasted table degrades into a slightly empty grid instead of a failed export.
  // `reduce`, not `Math.max(...spread)`: the spread is an argument list, and it
  // throws RangeError above ~125 000 entries — taking the whole document export
  // down, and with it any folder export that includes the document.
  const width = body.reduce((m, row) => Math.max(m, row.length), 0);
  for (const row of body) {
    for (let i = 0; i < width; i += 1) if (row[i] === undefined) row[i] = {};
  }

  return [{
    table: {
      widths: new Array(width).fill('*'),
      headerRows: headerRow ? 1 : 0,
      body,
    },
    layout: TABLE_LAYOUT,
    margin: [0, 6, 0, 10],
  }];
}

/** One HTML block element → zero or more pdfmake content nodes. */
function blockToContent(node) {
  const tag = node.tagName.toLowerCase();
  switch (tag) {
    case 'h1': return [{ text: inlineRuns(node), style: 'h1' }];
    case 'h2': return [{ text: inlineRuns(node), style: 'h2' }];
    case 'h3': return [{ text: inlineRuns(node), style: 'h3' }];
    case 'h4':
    case 'h5':
    case 'h6': return [{ text: inlineRuns(node), style: 'h4' }];
    case 'ul': return listItems(node, false);
    case 'ol': return listItems(node, true);
    case 'blockquote':
      return [{ text: inlineRuns(node), style: 'quote', margin: [16, 6, 0, 10] }];
    case 'pre':
      // A SINGLE-CELL TABLE, not a styled paragraph.
      //
      // `background` on a pdfmake text node tints only the glyph runs, so a
      // code block came out as grey-behind-the-words with no box — nothing like
      // the editor's bordered, filled block, which is what "the code does not
      // look similar on the pdf export" meant. A one-cell table is the only
      // primitive pdfmake has that fills a rectangle and flows text inside it.
      //
      // Whitespace is the content in a code block — never collapsed.
      return [{
        table: {
          widths: ['*'],
          body: [[{ text: node.textContent || '', style: 'code', margin: [6, 5, 6, 5] }]],
        },
        layout: CODE_BLOCK_LAYOUT,
        margin: [0, 6, 0, 10],
      }];
    case 'hr':
      return [{
        canvas: [{ type: 'line', x1: 0, y1: 3, x2: 515, y2: 3, lineWidth: 0.7, lineColor: '#d4d4d8' }],
        margin: [0, 8, 0, 12],
      }];
    case 'img': return imageBlock(node);
    case 'iframe': return videoLinkBlock(node);
    case 'table': return tableBlock(node);
    case 'div':
      if (node.hasAttribute('data-callout')) return calloutPdfBlock(node, inlineRuns(node));
    // falls through — any other div is a wrapper
    case 'section':
    case 'article': {
      // A wrapper — descend, so its children keep their own block styles.
      const out = [];
      for (const child of node.children) out.push(...blockToContent(child));
      if (out.length) return out;
      return textOf(node).trim() ? [{ text: inlineRuns(node), margin: [0, 0, 0, 8] }] : [];
    }
    default: {
      const runs = inlineRuns(node);
      return runs.length ? [{ text: runs, margin: [0, 0, 0, 8] }] : [];
    }
  }
}

/**
 * One document's BODY as pdfmake content nodes — no title, no page setup.
 *
 * Split out so the folder export (documentFolderPdf.js) concatenates the exact
 * same mapping rather than reimplementing the walk. A second HTML→pdfmake
 * mapper would drift from this one silently: the bug would be a heading style
 * or a nested list that renders correctly in a single-document export and
 * wrongly in a combined one, which nobody would think to compare.
 */
export function documentBodyContent(bodyHtml = '') {
  const host = document.createElement('div');
  host.innerHTML = sanitizeRichTextHtml(bodyHtml || '');

  const content = [];
  for (const child of host.children) content.push(...blockToContent(child));
  // A body with no block elements at all (plain text) still has words.
  if (!content.length) {
    const bare = (host.textContent || '').trim();
    if (bare) content.push({ text: bare });
  }
  return content;
}

/**
 * Page number for the footer band.
 *
 * A FUNCTION, not a static node: pdfmake calls it once per page with the live
 * (currentPage, pageCount), which is the only way to get "3 / 12" — a fixed
 * footer node would render the same text on every page.
 *
 * The count is trustworthy even with a table of contents, because pdfmake
 * already lays the document out twice to resolve TOC page references; the
 * numbering here and the numbers the TOC points at come from the same pass, so
 * they cannot disagree.
 *
 * Drawn in the bottom margin band (54pt), so it never collides with body text —
 * no margin change was needed.
 */
function pageFooter(currentPage, pageCount) {
  return {
    text: `${currentPage} / ${pageCount}`,
    alignment: 'center',
    fontSize: 8.5,
    color: '#9ca3af',
    margin: [0, 16, 0, 0],
  };
}

/** The shared page setup and style table, so both exports look identical. */
export const PDF_PAGE_SETUP = {
  pageSize: 'A4',
  pageMargins: [48, 54, 48, 54],
  // Lives here rather than in either builder, so a single-document export and a
  // combined folder export are numbered the same way. Splitting it would let
  // the two drift, which is the reason this object exists at all.
  footer: pageFooter,
  defaultStyle: { font: 'Roboto', fontSize: 10.5, lineHeight: 1.35 },
  styles: {
    title: { fontSize: 20, bold: true, margin: [0, 0, 0, 14] },
    h1: { fontSize: 16, bold: true, margin: [0, 12, 0, 6] },
    h2: { fontSize: 14, bold: true, margin: [0, 10, 0, 5] },
    h3: { fontSize: 12.5, bold: true, margin: [0, 9, 0, 4] },
    h4: { fontSize: 11.5, bold: true, margin: [0, 8, 0, 4] },
    quote: { italics: true, color: '#52525b' },
    // No `font: 'Courier'` — 0.3 registers only the families named in
    // `pdfMake.fonts`, and asking for an unregistered one throws the same
    // "not found in virtual file system" error the vfs bug produced.
    code: { fontSize: 9.5, color: '#18181b', background: '#f4f4f5' },
  },
};

/** Sanitized HTML → a pdfmake docDefinition. Exported for testing. */
export function buildPdfDocDefinition({ title = 'Untitled', bodyHtml = '' } = {}) {
  const content = [
    { text: title || 'Untitled', style: 'title' },
    ...documentBodyContent(bodyHtml),
  ];

  return {
    info: { title: title || 'Untitled' },
    content,
    ...PDF_PAGE_SETUP,
  };
}

/** The PDF as bytes. Exported separately so the save path stays testable. */
export async function renderDocumentPdf(doc) {
  const pdfMake = await loadPdfMake();
  // Encrypted images -> data: URIs BEFORE the sync mapper runs (this is the
  // one async seam; buildPdfDocDefinition/documentBodyContent stay sync).
  const bodyHtml = await resolveExportBodyHtml(doc?.bodyHtml);
  const generator = pdfMake.createPdf(buildPdfDocDefinition({ ...doc, bodyHtml }));
  // pdfmake 0.3 `getBuffer()` RETURNS A PROMISE. 0.1/0.2 took a callback, and
  // passing one here does not throw — the callback is simply never invoked, so
  // the export hangs forever with no error. `Promise.resolve` accepts either
  // shape, so this survives a downgrade too.
  const buffer = await Promise.resolve(generator.getBuffer());
  return new Uint8Array(buffer);
}
